import Permission from "Components/Common/Permission";
import TableContainer from "Components/Common/TableContainer";
import { normalize } from "common/lib/normalize";
import { getSortObj } from "common/lib/tableDataOptions";
import { getAgencies } from "common/services/agencies.service";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
} from "reactstrap";

const AgencyModal = ({ isOpen, toggle, title, filter }: any) => {
  const [params] = useState({
    page: 1, // Starts from 1
    pageSize: 20, // Default page size
  });

  const [pagination, setPagination] = useState({
    pageIndex: params.page - 1, // Convert to zero-based index
    pageSize: params.pageSize,
  });

  // Update pageSize
  // const setPageSize = (newPageSize: number) => {
  //   setPagination((prevPagination) => ({
  //     ...prevPagination,
  //     pageSize: newPageSize,
  //   }));
  // };

  const [sorted, setSorted] = useState(() => {
    return [{ id: "createdAt", desc: true }];
  });

  const [filterValue, setFilterValue] = useState(() => {
    return {
      agency: filter?.agency || null,
      status: filter?.status || "all",
      region: filter?.region || null,
      mda: filter?.mda || null,
      positionType: filter?.positionType || null,
      paymentCategory: filter?.paymentCategory || null,
      thematicArea: filter?.thematicArea || null,
      sector: filter?.sector || null,
      dateRange: filter?.dateRange || null,
    };
  });

  useEffect(() => {
    setFilterValue(() => {
      return {
        agency: filter?.agency || null,
        status: filter?.status || "all",
        region: filter?.region || null,
        mda: filter?.mda || null,
        positionType: filter?.positionType || null,
        paymentCategory: filter?.paymentCategory || null,
        thematicArea: filter?.thematicArea || null,
        sector: filter?.sector || null,
        dateRange: filter?.dateRange || null,
      };
    });
  }, [filter]);

  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState<any>({});

  const onPageChange = useCallback(
    async ({
      page,
      sorted,
      searchValue,
      filterValue,
    }: {
      page: number;
      sorted: Array<{ id: string; desc: boolean }>;
      searchValue: string;
      filterValue?: any;
    }) => {
      const sortObj = getSortObj(sorted);

      let andConditions = [];

      if (filterValue?.agency?.value) {
        andConditions.push({
          payments: {
            agency: {
              id: {
                $eq: filterValue.agency.value,
              },
            },
          },
        });
      }
      if (filterValue?.region?.value) {
        andConditions.push({
          payments: {
            mda: {
              region: {
                id: {
                  $eq: filterValue.region.value,
                },
              },
            },
          },
        });
      }
      if (filterValue?.mda?.value) {
        andConditions.push({
          payments: {
            mda: {
              id: {
                $eq: filterValue.mda.value,
              },
            },
          },
        });
      }
      if (filterValue?.paymentCategory?.value) {
        andConditions.push({
          payments: {
            paymentCategory: {
              id: {
                $eq: filterValue.paymentCategory.value,
              },
            },
          },
        });
      }
      if (filterValue?.thematicArea?.value) {
        andConditions.push({
          payments: {
            thematicArea: {
              id: {
                $eq: filterValue.thematicArea.value,
              },
            },
          },
        });
      }
      if (filterValue?.sector?.value) {
        andConditions.push({
          payments: {
            sector: {
              id: {
                $eq: filterValue.sector.value,
              },
            },
          },
        });
      }
      if (filterValue?.positionType?.value) {
        andConditions.push({
          payments: {
            positionType: {
              id: {
                $eq: filterValue.positionType.value,
              },
            },
          },
        });
      }
      if (filterValue?.dateRange?.fromDate && filterValue?.dateRange?.toDate) {
        const fromDate =
          filterValue.dateRange.fromDate instanceof Date
            ? filterValue.dateRange.fromDate
            : new Date(filterValue.dateRange.fromDate);

        const toDate =
          filterValue.dateRange.toDate instanceof Date
            ? filterValue.dateRange.toDate
            : new Date(filterValue.dateRange.toDate);
        andConditions.push({
          $or: [
            {
              payments: {
                startDate: {
                  $between: [
                    fromDate.toLocaleDateString("en-CA"),
                    toDate.toLocaleDateString("en-CA"),
                  ],
                },
              },
            },
            {
              payments: {
                endDate: {
                  $between: [
                    fromDate.toLocaleDateString("en-CA"),
                    toDate.toLocaleDateString("en-CA"),
                  ],
                },
              },
            },
          ],
        });
      }

      try {
        setLoading(true);

        const response: any = await getAgencies({
          pagination: {
            page,
            pageSize: pagination.pageSize,
          },
          sort: sortObj,
          populate: {
            payments: {
              count: true,
            },
          },
          filters: {
            payments: {
              id: {
                $notNull: true,
              },
            },
            $and: andConditions,
          },
        });
        setData(normalize(response.data));
        setMeta(response?.meta);
      } catch (e: any) {
        toast.error("Error while fetching data", {});
      } finally {
        setLoading(false);
      }
    },
    [pagination.pageSize]
  );

  const columns = useMemo(
    () => [
      {
        header: "Reporting Agency",
        accessorKey: "name",
        id: "name",
        enableColumnFilter: false,
        cell: (cell: any) => {
          const rowData = cell.row.original;

          return (
            <>
              <div
                className="d-flex align-items-center"
                style={{
                  // whiteSpace: "pre-wrap",
                  width: "400px",
                }}
              >
                <div className="flex-shrink-0 me-3">
                  <div className="avatar-sm">
                    <div className="avatar-title border bg-primary-subtle text-danger rounded fs-24">
                      <i className="ri-earth-line text-primary"></i>
                    </div>
                  </div>
                </div>
                <div className="flex-grow-1">
                  <h5 className="fs-14 mb-1" style={{ whiteSpace: "pre-wrap" }}>
                    <Permission
                      alternative={
                        <Link
                          to={`/reporting-agencies/${rowData?.id}`}
                          className="text-body"
                        >
                          {cell.getValue()}
                        </Link>
                      }
                      action="findOne"
                      resource="mda"
                    >
                      <Link
                        to={`/reporting-agencies/${rowData.id}`}
                        className="text-body"
                      >
                        {cell.getValue()}
                      </Link>
                    </Permission>
                  </h5>
                  <p className="text-muted mb-0">
                    <span className="fw-medium">
                      {cell.row.original?.fullName || ""}
                    </span>
                  </p>
                </div>
              </div>
            </>
          );
        },
      },

      {
        header: "Number of Contracts",
        accessorKey: "payments.count",
        enableColumnFilter: false,
        enableSorting: false,
        id: "payments.count",
        cell: (cell: any) => {
          const rowData = cell.row.original;
          const count = rowData?.payments?.count || 0;
          if (count === 0) {
            return (
              <span className="badge bg-danger-subtle text-danger fs-11">
                No Contracts
              </span>
            );
          }
          if (count === 1) {
            return <>{count} Contract</>;
          }
          return <>{count} Contracts</>;
        },
      },

      // {
      //   header: "Status",
      //   accessorKey: "status",
      //   enableColumnFilter: false,
      //   enableSorting: false,
      //   cell: (cell: any) => {
      //     const status = isPaymentActive(
      //       cell.row.original.startDate || "",
      //       cell.row.original.endDate || ""
      //     );
      //     return (
      //       <React.Fragment>
      //         {status === null ? (
      //           <span className="badge bg-warning-subtle text-warning fs-11">
      //             N/A
      //           </span>
      //         ) : status ? (
      //           <span className="badge bg-success-subtle text-success fs-11">
      //             Active
      //           </span>
      //         ) : status === false ? (
      //           <span className="badge bg-danger-subtle text-danger fs-11">
      //             Not Active
      //           </span>
      //         ) : null}
      //       </React.Fragment>
      //     );
      //   },
      // },
    ],
    []
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="xl"
      style={{ maxWidth: "95%", width: "95%", margin: "20px auto" }}
    >
      <ModalHeader toggle={toggle}>{title}</ModalHeader>
      <ModalBody>
        <Card>
          <CardHeader>
            <Row className="align-items-center g-3">
              <Col md={10}>
                <h5 className="card-title mb-1">Reporting Agencies</h5>
                <p className="mb-0 text-muted">
                  This page shows a list of reporting agencies and donors that
                  contribute information to the CIRT system.
                </p>
              </Col>
              <div className="col-md-auto ms-auto">
                <div className="d-flex gap-2"></div>
              </div>
            </Row>
          </CardHeader>
          <CardBody>
            <TableContainer
              loading={loading}
              columns={columns}
              data={data || []}
              pagination={pagination}
              setPagination={setPagination}
              sorted={sorted}
              setSorted={setSorted}
              onPageChange={onPageChange}
              FilterSection={() => {}}
              totalRows={meta?.pagination?.total || 0}
              totalPages={meta?.pagination?.pageCount || 0}
              filterValue={filterValue}
              divClass="table-responsive table-card mb-4"
              tableClass="align-middle table-nowrap mb-0 table-striped"
              theadClass="table-light table-nowrap"
            />
          </CardBody>
        </Card>
      </ModalBody>
    </Modal>
  );
};

export default AgencyModal;
